/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import { GroupedDeliverySeries } from "../types";

interface DeliveryOverviewRow {
  id: number;
  materialName: string;
  deliveredMassMtd: number;
  deliveredFraction: number | null;
}

interface DeliveriesOverviewTableProps {
  deliverySeries: GroupedDeliverySeries;
  selectedDate: Date | null;
}

export const DeliveriesOverviewTable = ({
  deliverySeries,
  selectedDate,
}: DeliveriesOverviewTableProps) => {
  const { t } = useTenantTranslation();

  const rows = useMemo(
    () => buildRows(deliverySeries, selectedDate),
    [deliverySeries, selectedDate]
  );

  const totalsRow = useMemo(() => buildTotalsRow(rows, t), [rows, t]);

  const columns = useColumns(rows, totalsRow, t);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={{ bottom: [totalsRow] }}
        columns={columns}
      />
    </Box>
  );
};

const buildRows = (
  deliverySeries: GroupedDeliverySeries,
  selectedDate: Date | null
): DeliveryOverviewRow[] => {
  const rawRows: Omit<DeliveryOverviewRow, "deliveredFraction">[] = [];
  let rowId = 0;

  for (const [key, series] of deliverySeries.values) {
    for (const [index, dateString] of series.date.entries()) {
      const date = new Date(dateString);
      if (!selectedDate || date.toISOString() === selectedDate.toISOString()) {
        const deliveredMassMtd = series.deliveryMassMtd[index] ?? 0;

        if (deliveredMassMtd > 0) {
          rawRows.push({
            id: rowId,
            materialName: key.materialName,
            deliveredMassMtd,
          });
          rowId++;
        }
      }
    }
  }

  const totalMass = rawRows.reduce((sum, row) => sum + row.deliveredMassMtd, 0);
  return rawRows.map((row) => ({
    ...row,
    deliveredFraction: totalMass > 0 ? row.deliveredMassMtd / totalMass : null,
  }));
};

const buildTotalsRow = (
  rows: DeliveryOverviewRow[],
  t: TFunction
): DeliveryOverviewRow => {
  const totalMass = rows.reduce((sum, row) => sum + row.deliveredMassMtd, 0);
  return {
    id: rows.length,
    materialName: t("Total"),
    deliveredMassMtd: totalMass,
    deliveredFraction: totalMass > 0 ? 1 : null,
  };
};

const useColumns = (
  rows: DeliveryOverviewRow[],
  totalsRow: DeliveryOverviewRow,
  t: TFunction
): GridColDef[] => {
  const units = useUnitsFormatter(true);
  const theme = useTheme();
  const formatNumber = useFormatNumber();

  return [
    {
      field: "materialName",
      headerName: t("material"),
      flex: 2,
      valueFormatter: (value?: string) => (!value ? "" : t(value)),
    },
    {
      field: "deliveredMassMtd",
      type: "number",
      headerName: `${t("deliveries")}${units("mass")}`,
      flex: 2,
      align: "right",
      renderCell: (
        params: GridRenderCellParams<DeliveryOverviewRow, number>
      ) =>
        params.row.id === rows.length ? (
          <div>{formatNumber(0, true)(totalsRow.deliveredMassMtd)}</div>
        ) : (
          <BarCell
            value={params.value!}
            formattedValue={formatNumber(0, true)(params.value)}
            maxValue={totalsRow.deliveredMassMtd}
            color={theme.palette.data.blue}
          />
        ),
    },
    {
      field: "deliveredFraction",
      type: "number",
      headerName: `${t("deliveries")} (%)`,
      flex: 1,
      align: "right",
      valueFormatter: (value?: number | null) =>
        value == null ? "" : `${formatNumber(1, true)(value * 100)} %`,
    },
  ];
};
